import React, { FC, useEffect, useRef, useState } from 'react'
import AppPageComponent from '@components/page/appPage.component'
import PageHeaderComponent from '@components/page/pageHeader.component'
import {
  Button,
  Col,
  FormControl,
  InputGroup,
  Row,
  Spinner,
} from 'react-bootstrap'
import { useTheme } from '@config/theme.config'
import {
  BsBoxArrowInRight,
  BsCircleFill,
  BsFileEarmarkArrowDown,
  BsMusicNote,
  BsSoundwave,
} from 'react-icons/bs'
import AreaComponent from '@components/area/area.component'
import {
  AreaModel,
  fetchAllAreas,
  selectAllAreas,
} from '@store/slices/area.slice'
import {
  BroadcastModel,
  fetchAllBroadcasts,
  selectAllBroadcasts,
  updateBroadcast,
} from '@store/slices/broadcast.slice'
import {
  fetchAllStations,
  selectAllStations,
  StationModel,
} from '@store/slices/station.slice'
import ClockComponent from '@components/broadcast/clockComponent'
import {
  formatDate,
  formatDjangoTimeString,
  formatSeconds,
  getCharToTimeLength,
  getHourNow,
  hasDatePassed,
  isToday,
} from '../tools/time.tools'
import ReactSelect from 'react-select'
import { reactSelectDarkmode } from '@config/slate.config'
import { EntityId, unwrapResult } from '@reduxjs/toolkit'
import { getSelectButtonClass } from '../tools/style.tools'
import { useDispatch, useSelector } from 'react-redux'
import { fetchAllAudios, selectAllAudios } from '@store/slices/audio.slice'
import {fetchAllVoices, selectAllVoices, selectVoiceLoading} from '@store/slices/voice.slice'
import { AppDispatch } from '@store/store'
import { DjangoFilter } from '@store/slices/generic.slice'
import { getStateColor } from '../tools/newsState.tools'
import {clearAllNews, fetchAllNewss, NewsModel, selectAllNews} from '@store/slices/news.slice'
import {
  fetchAllTimeslots,
  fetchTimeslotById,
  selectAllSlots,
} from '@store/slices/timeslot.slice'
import { fetchAllJingles, selectAllJingles } from '@store/slices/jingle.slice'
import {
  duplicateBroadcast,
  generateAudioForBroadcast,
  publishBroadcast,
} from '@store/thunk/broadcast.thunk'
import AudioPlayButtonComponent from '@components/shared/audioPlayButtonComponent'
import { toast } from 'react-toastify'

const BroadcastPage: React.FC = () => {
  const { theme } = useTheme()
  const dispatch = useDispatch<AppDispatch>()
  const stations = useSelector(selectAllStations)
  const areas: AreaModel[] = useSelector(selectAllAreas)
  const audios = useSelector(selectAllAudios)
  const newss = useSelector(selectAllNews)
  const jingles = useSelector(selectAllJingles)
  const slots = useSelector(selectAllSlots)
  const broadcasts: BroadcastModel[] = useSelector(selectAllBroadcasts)
  const isVoicesLoading = useSelector(selectVoiceLoading)

  /// STATE
  const [dayFilter, setDayFilter] = useState(formatDate(new Date()))
  /// STATE
  const [selectedStation, setSelectedStation] = useState<
    StationModel | undefined
  >(undefined)
  const defaultAreaId = selectedStation?.radioPreset.defaultAreaId
  const [shouldFetchBroadcasts, setShouldFetchBroadcasts] = useState<boolean>(true)

  const stationsAreas: AreaModel[] = areas
    .filter((area) => area.stationId === selectedStation?.id)
    .sort((a, b) =>
      b.id === selectedStation?.radioPreset.defaultAreaId
        ? 1
        : a.position - b.position
    )

  const findLatestBroadcastHour = (station: StationModel): number => {
    return Math.max(
      ...(station.radioPreset.broadcastingHours.map(Number) || [])
    )
  }

  const findSmallestGreaterThan = (
    array: string[],
    x: number,
    max: number | typeof Infinity = Infinity
  ): string | null => {
    const result = array
      .map(Number)
      .filter((num) => num > x)
      .reduce((min, cur) => (cur < min ? cur : min), max)
    return result !== Infinity ? result.toString() : max.toString()
  }
  const getDefaultHour = (): string => {
    if (hasDatePassed(dayFilter)) {
      return ''
    } else if (isToday(new Date(dayFilter))) {
      return selectedStation?.radioPreset
        ? findSmallestGreaterThan(
            selectedStation?.radioPreset.broadcastingHours,
            getHourNow(),
            findLatestBroadcastHour(selectedStation)
          ) || ''
        : ''
    }
    return selectedStation?.radioPreset
      ? selectedStation?.radioPreset.broadcastingHours[0]
      : ''
  }

  /// STATE
  const [selectedHour, setSelectedHour] = useState<string>(getDefaultHour())
  const [publishing, setPublishing] = useState<boolean>(false)

  useEffect(() => {
    if (!shouldFetchBroadcasts) {
      setShouldFetchBroadcasts(true);
    }
  }, [selectedHour]);

  useEffect(() => {
    if (!selectedHour) {
      setSelectedHour(getDefaultHour());
    }
    if (!shouldFetchBroadcasts) {
      setShouldFetchBroadcasts(true);
    }
  }, [selectedStation, dayFilter])

  useEffect(() => {
    if (stations.length == 0) dispatch(fetchAllStations())
    if (areas.length == 0) dispatch(fetchAllAreas())
    if (jingles) dispatch(fetchAllJingles())

    refreshNewsForDay()
  }, [])

  useEffect(() => {
    if (stations.length > 0) setSelectedStation(stations[0])
    if (!shouldFetchBroadcasts) {
      setShouldFetchBroadcasts(true);
    }
  }, [stations])

  useEffect(() => {
    if (
      shouldFetchBroadcasts &&
      !!dayFilter &&
      !!selectedStation &&
      !!selectedHour &&
      isFinite(parseInt(selectedHour, 10))
    ) {
      setShouldFetchBroadcasts(false);
      const hourFilter: DjangoFilter<BroadcastModel> = new DjangoFilter()
      const date = new Date(dayFilter)
      date.setHours(parseInt(selectedHour, 10))
      hourFilter.addFilter({
        datetime: date.toISOString(),
        area__radio_station_id: selectedStation.id,
      })
      dispatch(fetchAllBroadcasts(hourFilter))
        .then(unwrapResult)
        .then(() => {
          const broadcastFilter = new DjangoFilter()
          broadcastFilter.addFilter({
            broadcast__datetime: date.toISOString(),
          })
          dispatch(fetchAllTimeslots(broadcastFilter))
        })
        .catch((e) => console.error(e))

      refreshNewsForDay()
    }
  }, [selectedStation, dayFilter, selectedHour, shouldFetchBroadcasts])

  const refreshNewsForDay = () => {
    dispatch(clearAllNews({}))
    const filter = new DjangoFilter<NewsModel>()
    filter.addDateFilter('date', new Date(dayFilter), new Date(dayFilter))
    dispatch(fetchAllNewss(filter))
  }

  const isHourPassed = (hourString: string): boolean => {
    const dayFilterDate = new Date(dayFilter)
    if (isToday(dayFilterDate)) {
      return getHourNow() >= Number.parseInt(hourString)
    }
    return false
  }

  const StationFilter = () => (
    <div className="d-flex flex-nowrap text-nowrap gap-1">
      {stations.map((station) => (
        <Button
          key={station.id}
          disabled={selectedStation?.id == station.id}
          variant={'outline-secondary'}
          className={getSelectButtonClass(
            selectedStation?.id == station.id,
            theme
          )}
          onClick={() =>
            setSelectedStation(
              stations.find((selectStation) => selectStation.id == station.id)
            )
          }
        >
          {station.name}
        </Button>
      ))}
    </div>
  )

  const DayFilter = () => (
    <InputGroup>
      <FormControl
        type="date"
        className="bg-dark-subtle fw-bold"
        value={dayFilter}
        onChange={(e) => setDayFilter(e.target.value)}
      ></FormControl>
    </InputGroup>
  )

  const HourFilter = () => (
    <div className="d-flex">
      {selectedStation?.radioPreset?.broadcastingHours.map(
        (playHour: string) => (
          <InputGroup key={playHour}>
            <Button
              variant={'outline-secondary'}
              className={`fw-bold me-1 py-0 rounded-2 ${getSelectButtonClass(selectedHour == playHour, theme)} ${isHourPassed(playHour) ? 'text-secondary-emphasis' : ''}`}
              style={{ maxHeight: '28px' }}
              onClick={() => setSelectedHour(playHour)}
            >
              {playHour}
            </Button>
          </InputGroup>
        )
      )}
    </div>
  )

  const TimeAndStatusBar = () => (
    <div
      className={`button rounded-2 bg-light-subtle d-flex align-items-center gap-3 text-bg-${theme} border border-1 py-1 px-2 mx-auto z-3`}
      style={{
        width: 'fit-content',
        borderColor: '#212529',
      }}
    >
      {stationsAreas.map((area) => {
        const fixedHour =
          selectedHour.toString().length < 2 ? '0' + selectedHour : selectedHour
        const broadcast = broadcasts.find(
          (broadcast) =>
            broadcast.areaId == area.id &&
            broadcast.date.startsWith(`${dayFilter} ${fixedHour}`)
        )
        const status = broadcast?.published
          ? 'published'
          : broadcast?.audioId
            ? 'review'
            : 'progress'
        return (
          <span key={area.id} className="d-flex align-items-center" style={{fontSize: '0.9rem'}}>
            <BsCircleFill
              size="0.5em"
              className={`text-${status} me-1`}
            ></BsCircleFill>
            {area.shortcut}
          </span>
        )
      })}
      <ClockComponent
        selectedDay={dayFilter}
        selectedHour={selectedHour}
      ></ClockComponent>
    </div>
  )

  const AreaHeader: FC<{ area: AreaModel }> = ({ area }) => {
    const voices = useSelector(selectAllVoices)
    const fixedHour =
      selectedHour.toString().length < 2 ? '0' + selectedHour : selectedHour
    const broadcast = broadcasts.find(
      (broadcast) =>
        broadcast.areaId == area.id &&
        broadcast.date.startsWith(`${dayFilter} ${fixedHour}`)
    )
    const broadcastAudio = audios.find(
      (audio) => audio.id == broadcast?.audioId
    )
    const [audioGenerating, setAudioGenerating] = useState<boolean>(false)
    useEffect(() => {
      if (voices.length === 0 && !isVoicesLoading) dispatch(fetchAllVoices())
    }, [])

    const voiceRate = voices.find(
      (voice) => voice.id == broadcast?.voiceId
    )?.readingSpeed
    const headerLength =
      broadcast && voiceRate
        ? getCharToTimeLength(
            JSON.parse(broadcast?.headers).reduce(
              (
                totalCount: number,
                item: {
                  text: string
                }
              ) => {
                return totalCount + item.text.length
              },
              0
            ),
            Number(voiceRate)
          )
        : 0
    let newsLength = 0

    broadcast?.slots
      .map((slotId) => slots.find((slot) => slot.id == slotId))
      .forEach((slot) => {
        const news = newss.find((news) => news.id === slot?.newsId)
        const jingle = jingles.find((jingle) => jingle.id === slot?.jingleId)
        const audio = audios.find((audio) => audio.id === news?.audioId)
        if (audio?.length) {
          const totalSeconds = formatDjangoTimeString(audio?.length)
          newsLength += Number(totalSeconds)
        }
        if (jingle?.length) {
          const totalSeconds = formatDjangoTimeString(jingle?.length)
          newsLength += Number(totalSeconds)
        }
      })

    const broadcastLength = newsLength + headerLength
    const onGenerateAudioClick = () => {
      if (broadcast) {
        setAudioGenerating(true)
        toast.info('Generuji audio pro relaci')
        dispatch(generateAudioForBroadcast(broadcast.id))
          .then(unwrapResult)
          .then(() => toast.success('Audio vygenerováno'))
          .catch((e) => console.error(e))
          .finally(() => setAudioGenerating(false))
      }
    }

    const onDownloadAudioClick = () => {
      if (broadcastAudio?.publicPath) {
        const link = document.createElement('a')
        link.href = broadcastAudio?.publicPath
        link.download = `relace-${selectedStation?.name}-${area?.name}-${broadcast?.date}.wav` // TODO hardcoded wav
        document.body.appendChild(link) // Needed for Firefox
        link.click()
        document.body.removeChild(link)
      }
    }

    const onDuplicateClick = () => {
      if (broadcast && selectedStation?.radioPreset.defaultAreaId)
        dispatch(
          duplicateBroadcast({
            copyTo: broadcast.id,
          })
        )
          .then(unwrapResult)
          .then((broadcast) => {
            toast.info('Relace duplikována')
            broadcast.slots.forEach((slotId) =>
              dispatch(fetchTimeslotById(slotId))
            )
          })
          .catch((e) => console.error(e))
    }

    const onPublishClick = () => {
      if (broadcast) {
        toast.info('Začala publikace')
        setPublishing(true)
        dispatch(publishBroadcast(broadcast.id))
          .then(unwrapResult)
          .then(() => toast.success('Publikace proběhla úspěšně'))
          .catch((e) => toast.error(e))
          .finally(() => setPublishing(false))
      }
    }

    const onBroadcastVoiceChange = (voiceId: EntityId | null) => {
      if (broadcast?.id && voiceId)
        dispatch(
          updateBroadcast({
            id: broadcast.id,
            data: {
              ...broadcast,
              voiceId,
            },
          })
        )
    }

    const VoiceSelect = () => {
      const voiceOptions = voices.map((voice) => ({
        value: voice.id,
        label: voice.name,
      }))
      return (
        <ReactSelect
          defaultValue={
            broadcast?.voiceId
              ? voiceOptions.find(
                  (option) => option.value === broadcast?.voiceId
                )
              : null
          }
          placeholder={'Moderátor'}
          className={'px-0'}
          options={voiceOptions}
          styles={reactSelectDarkmode(theme)}
          onChange={(option) =>
            onBroadcastVoiceChange(option ? option.value : null)
          }
        />
      )
    }
    return (
      <Col style={{ minWidth: '470px', maxWidth: '35%' }}>
        <Row className="justify-content-between">
          <Col
            className={`fs-5 fw-bold text-${theme == 'light' ? 'dark' : 'light'} flex-grow-0 me-2 p-0 text-nowrap`}
          >
            {area.name}
          </Col>
          <Col className="d-flex flex-grow-0 flex-nowrap gap-1 px-0">
            {defaultAreaId && defaultAreaId != area.id && (
                <Button
                    onClick={onDuplicateClick}
                    title={'Duplikovat'}
                    variant={'warning'}
                    className=" d-flex align-items-center px-2"
                >
                  <BsBoxArrowInRight
                      style={{ width: '24px', height: '24px' }}
                  ></BsBoxArrowInRight>
                </Button>
            )}
            {
              <Button
                onClick={onPublishClick}
                variant={broadcast?.published ? 'success' : 'primary'}
                className="fw-bold d-flex align-items-center"
                disabled={!broadcastAudio || publishing}
              >
                {!publishing && (
                  <span>
                    {broadcast?.published ? 'Publikováno' : 'Publikovat'}
                  </span>
                )}
                {publishing && <span>{'Publikuji..'}</span>}
              </Button>
            }
          </Col>
        </Row>
        <Row className={'w-100 mt-2 d-flex flex-row flex-nowrap justify-content-between'}>
          <Col className={'flex-grow-0'}>
            <VoiceSelect></VoiceSelect>
          </Col>
          <Col className={'d-flex flex-grow-1 flex-nowrap gap-1 px-0'}>
            <Button
                onClick={onGenerateAudioClick}
                title={'Vygenerovat audio'}
                variant={'light'}
                className="px-2 fw-bold d-flex align-items-center"
                style={{ width: '40px', height: '40px' }}
            >
              {!audioGenerating && (
                  <BsSoundwave
                      style={{ width: '24px', height: '24px' }}
                  ></BsSoundwave>
              )}
              {audioGenerating && (
                  <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      style={{ width: '24px', height: '24px' }}
                  />
              )}
            </Button>
            <Button
                onClick={onDownloadAudioClick}
                title={'Stáhnout'}
                variant={'secondary'}
                className="d-flex align-items-center px-2"
                disabled={!broadcastAudio}
                style={{ width: '40px', height: '40px' }}
            >
              <BsFileEarmarkArrowDown
                  style={{ width: '24px', height: '24px' }}
              ></BsFileEarmarkArrowDown>
            </Button>
            <AudioPlayButtonComponent
                title={'Přehrát relaci'}
                audioId={broadcast?.audioId || null}
            ></AudioPlayButtonComponent>
          </Col>
        </Row>
        <Row>
          <div className="bg-opacity-75 bg-light text-dark rounded-1 px-2 py-1 mt-2 d-flex fw-bold gap-4">
            <span>Headlines (est): {formatSeconds(headerLength)}</span>
            <span>Zprávy: {formatSeconds(newsLength)}</span>
            <span>Total { !broadcastAudio && '(est)' }: {formatSeconds(!!broadcastAudio ? formatDjangoTimeString(broadcastAudio.length) : broadcastLength)}</span>
          </div>
        </Row>
      </Col>
    )
  }

  const AreaBody: FC<{ area: AreaModel }> = ({ area }) => {
    const fixedHour =
      selectedHour.toString().length < 2 ? '0' + selectedHour : selectedHour
    const broadcast = broadcasts.find(
      (broadcast) =>
        broadcast.areaId == area.id &&
        broadcast.date.startsWith(`${dayFilter} ${fixedHour}`)
    )
    return broadcast && selectedStation?.radioPreset ? (
      <Col key={area.id} style={{ minWidth: '470px', maxWidth: '35%' }}>
        <AreaComponent
          areaId={area.id}
          broadcastId={broadcast.id}
        ></AreaComponent>
      </Col>
    ) : (
      <Col style={{ minWidth: '470px', maxWidth: '35%' }}>No broadcast found</Col>
    )
  }
  return (
    <AppPageComponent>
      <PageHeaderComponent>
        <Col className="d-flex py-2 gap-2">
          <Col className="flex-grow-0">
            <StationFilter></StationFilter>
          </Col>
          <Col className="flex-grow-0">
            <DayFilter></DayFilter>
          </Col>
          <Col className="d-flex align-items-center flex-grow-0">
            <HourFilter></HourFilter>
          </Col>
          <Col className="flex-grow-0">
            <TimeAndStatusBar></TimeAndStatusBar>
          </Col>
        </Col>
      </PageHeaderComponent>
      <Row className="mx-5 px-0">
        <Row className="overflow-x-scroll pt-3">
          <Row
              className="gap-2 px-0 mx-0 flex-nowrap position-relative"
          >
            {stationsAreas.map((area) => (
                <AreaHeader key={area.id} area={area}></AreaHeader>
            ))}
          </Row>
          <Row className=" gap-2 px-0 mx-0 flex-nowrap">
            {stationsAreas.map((area) => (
                <AreaBody key={area.id} area={area}></AreaBody>
            ))}
          </Row>
        </Row>
      </Row>
    </AppPageComponent>
  )
}

export default BroadcastPage
